import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { TextField } from '../ui/TextField';

import { Badge } from '../ui/badge';
import { 
  FeatherSave,
  FeatherPlus,
  FeatherX,
  FeatherFileText,
  FeatherUsers,
  FeatherCalendar,
  FeatherTag,
  FeatherGrid,
  FeatherClock,
  FeatherMail,
  FeatherPhone,
  FeatherUser,
  FeatherCheck,
  FeatherAlertCircle
} from '../ui/icons';
import api from '../../services/api';
import { useMasterDataArray, useMasterDataOptions } from '../../hooks/useMasterData';
import './NexGateForm.css';

const NexGateForm = ({ engagement, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    engagement_type: '',
    title: '',
    description: '',
    requester: '',
    priority: 'medium',
    required_roles: [],
    experience_level: 'mid',
    duration: '',
    start_date: '',
    notes: '',
    
    // Custom Software fields
    project_objectives: '',
    target_platform: '',
    deliverables: '',
    timeline: '',
    
    // Augmentation fields
    number_of_resources: '',
    tech_stack: [],
    client_contact_info: {
      name: '',
      email: '',
      phone: ''
    }
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Master data hooks
  const { options: engagementTypeOptions, loading: engagementTypeLoading } = useMasterDataOptions('engagement_types');
  const { options: priorityOptions, loading: priorityLoading } = useMasterDataOptions('priorities');
  const { options: experienceLevelOptions, loading: experienceLevelLoading } = useMasterDataOptions('experience_levels');
  const { values: availableRoles, loading: rolesLoading } = useMasterDataArray('roles');
  const { values: availableTechStacks, loading: techStackLoading } = useMasterDataArray('technology_stack');

  useEffect(() => {
    if (engagement) {
      console.log('Populating form with engagement data:', engagement);
      setFormData({
        ...engagement,
        start_date: engagement.start_date ? engagement.start_date.split('T')[0] : ''
      });
    } else {
      console.log('No engagement data - creating new engagement');
    }
  }, [engagement]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleArrayChange = (field, value, action = 'toggle') => {
    setFormData(prev => {
      const currentArray = prev[field] || [];
      let newArray;

      if (action === 'toggle') {
        if (currentArray.includes(value)) {
          newArray = currentArray.filter(item => item !== value);
        } else {
          newArray = [...currentArray, value];
        }
      } else if (action === 'add') {
        newArray = [...currentArray, value];
      } else if (action === 'remove') {
        newArray = currentArray.filter(item => item !== value);
      }

      return {
        ...prev,
        [field]: newArray
      };
    });
  };

  const handleNestedChange = (parentField, childField, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: value
      }
    }));
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    setUploading(true);

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post(`/api/nexgate/${engagement?.id || 'temp'}/attachments`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        setAttachments(prev => [...prev, response.data.data]);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setError('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate required fields
      if (!formData.engagement_type || !formData.title || !formData.requester || 
          !formData.required_roles.length || !formData.duration || !formData.start_date) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      // Validate conditional fields
      if (formData.engagement_type === 'custom_software') {
        if (!formData.project_objectives || !formData.target_platform || 
            !formData.deliverables || !formData.timeline) {
          setError('Custom software engagements require project objectives, target platform, deliverables, and timeline');
          setLoading(false);
          return;
        }
      }

      if (formData.engagement_type === 'client_augmentation') {
        if (!formData.number_of_resources || !formData.tech_stack.length) {
          setError('Client augmentation engagements require number of resources and tech stack');
          setLoading(false);
          return;
        }
      }

      const submitData = { ...formData };
      
      // Remove fields that shouldn't be sent to the API
      delete submitData.attachments;
      delete submitData.matches;
      delete submitData.comments;
      delete submitData.created_by_user;
      delete submitData.updated_by_user;
      
      // Clean up conditional fields based on type
      if (formData.engagement_type !== 'custom_software') {
        delete submitData.project_objectives;
        delete submitData.target_platform;
        delete submitData.deliverables;
        delete submitData.timeline;
      }

      if (formData.engagement_type !== 'client_augmentation') {
        delete submitData.number_of_resources;
        delete submitData.tech_stack;
        delete submitData.client_contact_info;
      }

      let response;
      
      if (engagement?.id) {
        // Update existing engagement
        console.log('Updating existing engagement:', engagement.id, submitData);
        response = await api.put(`/api/nexgate/${engagement.id}`, submitData);
      } else {
        // Create new engagement
        console.log('Creating new engagement:', submitData);
        response = await api.post('/api/nexgate', submitData);
      }
      
      onSuccess(response.data);
    } catch (error) {
      console.error('Error submitting form:', error);
      setError(error.response?.data?.error || 'Failed to submit engagement');
    } finally {
      setLoading(false);
    }
  };

  const renderMultiSelect = (field, options, label, placeholder) => (
    <div className="nexgate-form-group">
      <label className="nexgate-form-label">{label}</label>
      <div className="nexgate-form-multi-select-container">
        <div className="nexgate-form-selected-items">
          {formData[field]?.map(item => (
            <Badge key={item} variant="secondary" className="nexgate-form-selected-item">
              {item}
              <button
                type="button"
                onClick={() => handleArrayChange(field, item, 'remove')}
                className="nexgate-form-remove-item"
              >
                <FeatherX size={12} />
              </button>
            </Badge>
          ))}
        </div>
        <select
          className="nexgate-form-select"
          onChange={(e) => {
            if (e.target.value) {
              handleArrayChange(field, e.target.value, 'add');
              e.target.value = '';
            }
          }}
        >
          <option value="">{placeholder}</option>
          {options.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderCustomSoftwareFields = () => (
    <div className="nexgate-form-section">
      <h3 className="nexgate-form-section-title">
        <FeatherGrid size={20} />
        Project Details
      </h3>
      <div className="nexgate-form-grid">
        <TextField label="Target Platform *">
          <TextField.Input
            value={formData.target_platform}
            onChange={(e) => handleInputChange('target_platform', e.target.value)}
            placeholder="e.g., Web, Mobile, Desktop, Cloud"
          />
        </TextField>
        
        <TextField label="Timeline *">
          <TextField.Input
            value={formData.timeline}
            onChange={(e) => handleInputChange('timeline', e.target.value)}
            placeholder="e.g., 3 months, 6 weeks, etc."
          />
        </TextField>
      </div>

      {/* Full-width Project Objectives */}
      <div className="nexgate-form-group">
        <label className="nexgate-form-label">Project Objectives *</label>
        <textarea
          className="nexgate-form-text-area"
          value={formData.project_objectives}
          onChange={(e) => handleInputChange('project_objectives', e.target.value)}
          placeholder="Describe the main objectives and goals of the project..."
          rows={4}
        />
      </div>

      {/* Full-width Deliverables */}
      <div className="nexgate-form-group">
        <label className="nexgate-form-label">Deliverables *</label>
        <textarea
          className="nexgate-form-text-area"
          value={formData.deliverables}
          onChange={(e) => handleInputChange('deliverables', e.target.value)}
          placeholder="List the expected deliverables..."
          rows={3}
        />
      </div>
    </div>
  );

  const renderAugmentationFields = () => (
    <div className="nexgate-form-section">
      <h3 className="nexgate-form-section-title">
        <FeatherUsers size={20} />
        Resource Requirements
      </h3>
      <div className="nexgate-form-grid">
        <TextField label="Number of Resources *">
          <TextField.Input
            type="number"
            min="1"
            value={formData.number_of_resources}
            onChange={(e) => handleInputChange('number_of_resources', e.target.value)}
            placeholder="e.g., 2"
          />
        </TextField>
        
        {renderMultiSelect('tech_stack', availableTechStacks, 'Tech Stack *', 'Select technologies...', techStackLoading)}
        
        <div className="nexgate-form-group">
          <label className="nexgate-form-label">Client Contact Information</label>
          <div className="nexgate-form-contact-info-grid">
            <TextField label="Contact Name">
              <TextField.Input
                value={formData.client_contact_info?.name || ''}
                onChange={(e) => handleNestedChange('client_contact_info', 'name', e.target.value)}
                placeholder="Contact person name"
              />
            </TextField>
            
            <TextField label="Email">
              <TextField.Input
                type="email"
                value={formData.client_contact_info?.email || ''}
                onChange={(e) => handleNestedChange('client_contact_info', 'email', e.target.value)}
                placeholder="contact@company.com"
              />
            </TextField>
            
            <TextField label="Phone">
              <TextField.Input
                value={formData.client_contact_info?.phone || ''}
                onChange={(e) => handleNestedChange('client_contact_info', 'phone', e.target.value)}
                placeholder="+1 (555) 123-4567"
              />
            </TextField>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="nexgate-form">
      <div className="nexgate-form-header">
        <h2 className="nexgate-form-title">
          <FeatherFileText size={24} />
          {engagement ? 'Edit Engagement' : 'Start a New Engagement – NexGate'}
        </h2>
        {error && (
          <div className="nexgate-form-error-message">
            <FeatherAlertCircle size={16} />
            {error}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="nexgate-form-engagement-form">
        {/* Basic Information */}
        <div className="nexgate-form-section">
          <h3 className="nexgate-form-section-title">
            <FeatherUser size={20} />
            Basic Information
          </h3>
          <div className="nexgate-form-grid">
            <div className="nexgate-form-group">
              <label className="nexgate-form-label">Engagement Type *</label>
              <select
                className="nexgate-form-select"
                value={formData.engagement_type}
                onChange={(e) => handleInputChange('engagement_type', e.target.value)}
                required
                disabled={engagementTypeLoading}
              >
                <option value="">{engagementTypeLoading ? 'Loading...' : 'Select engagement type...'}</option>
                {!engagementTypeLoading && engagementTypeOptions.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <TextField label="Title *">
              <TextField.Input
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter engagement title..."
                required
              />
            </TextField>

            <TextField label="Requester *">
              <TextField.Input
                value={formData.requester}
                onChange={(e) => handleInputChange('requester', e.target.value)}
                placeholder="Client name or internal department"
                required
              />
            </TextField>

            <div className="nexgate-form-group">
              <label className="nexgate-form-label">Priority *</label>
              <select
                className="nexgate-form-select"
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', e.target.value)}
                required
                disabled={priorityLoading}
              >
                {priorityLoading ? (
                  <option value="">Loading...</option>
                ) : (
                  priorityOptions.map(priority => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label}
                    </option>
                  ))
                )}
              </select>
            </div>

            {renderMultiSelect('required_roles', availableRoles, 'Required Roles *', 'Select roles...', rolesLoading)}

            <div className="nexgate-form-group">
              <label className="nexgate-form-label">Experience Level *</label>
              <select
                className="nexgate-form-select"
                value={formData.experience_level}
                onChange={(e) => handleInputChange('experience_level', e.target.value)}
                required
                disabled={experienceLevelLoading}
              >
                {experienceLevelLoading ? (
                  <option value="">Loading...</option>
                ) : (
                  experienceLevelOptions.map(level => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))
                )}
              </select>
            </div>

            <TextField label="Duration *">
              <TextField.Input
                value={formData.duration}
                onChange={(e) => handleInputChange('duration', e.target.value)}
                placeholder="e.g., 3 months, 6 weeks, etc."
                required
              />
            </TextField>

            <TextField label="Start Date *">
              <TextField.Input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                required
              />
            </TextField>
          </div>

          {/* Full-width Description */}
          <div className="nexgate-form-group">
            <label className="nexgate-form-label">Description *</label>
            <textarea
              className="nexgate-form-text-area"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe the engagement requirements, objectives, and key details..."
              rows={4}
              required
            />
          </div>

          {/* Full-width Notes */}
          <div className="nexgate-form-group">
            <label className="nexgate-form-label">Notes</label>
            <textarea
              className="nexgate-form-text-area"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Additional notes or requirements..."
              rows={3}
            />
          </div>
        </div>

        {/* Conditional Fields */}
        {formData.engagement_type === 'custom_software' && renderCustomSoftwareFields()}
        {formData.engagement_type === 'client_augmentation' && renderAugmentationFields()}

        {/* Attachments */}
        <div className="nexgate-form-section">
          <h3 className="nexgate-form-section-title">
            <FeatherFileText size={20} />
            Attachments
          </h3>
          <div className="nexgate-form-attachments-section">
            <input
              type="file"
              multiple
              onChange={handleFileUpload}
              disabled={uploading}
              className="nexgate-form-file-input"
              accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
            />
            {uploading && <div className="nexgate-form-uploading">Uploading...</div>}
            
            {attachments.length > 0 && (
              <div className="nexgate-form-attachments-list">
                {attachments.map(attachment => (
                  <div key={attachment.id} className="nexgate-form-attachment-item">
                    <FeatherFileText size={16} />
                    <span>{attachment.file_name}</span>
                    <span className="nexgate-form-file-size">({Math.round(attachment.file_size / 1024)}KB)</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className="nexgate-form-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={loading}
          >
            <FeatherX size={16} />
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="nexgate-form-submit-button"
          >
            {loading ? (
              <>
                <div className="nexgate-form-spinner"></div>
                Saving...
              </>
            ) : (
              <>
                <FeatherSave size={16} />
                {engagement ? 'Update Engagement' : 'Create Engagement'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default NexGateForm; 